import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete } from "components/forms";
import { DEAL_INDEX, indexNames } from "constants/reports";
import { useReportsMetaSelector } from "store";
import type { CardConfig, KpiCardConfig } from "store/types";

import { SectionLabel } from "./SectionLabel";

interface Metric {
  title: string;
  filter: string;
  indexName?: { index: string; name: string };
}

interface Props {
  config: KpiCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

const indexOptions = ["Use deal specific index", ...Object.values(indexNames)];

export const KpiSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { KPIFields, KPICharacteristics } = useReportsMetaSelector();

  const kpiOptions = useMemo(() => [...KPIFields, ...KPICharacteristics], [KPIFields, KPICharacteristics]);

  const metricsFromConfig: Metric[] = useMemo(() => {
    if (!config.cubics) return [];
    return Object.values(config.cubics).map((c) => ({
      title: c.title ?? "",
      filter: c.filter ?? "",
      ...(c.indexName && { indexName: c.indexName })
    }));
  }, [config.cubics]);

  const [draftMetrics, setDraftMetrics] = useState<Metric[]>(metricsFromConfig);

  const isHydratingRef = useRef(true);

  useEffect(() => {
    if (isHydratingRef.current) {
      setDraftMetrics(metricsFromConfig);
      isHydratingRef.current = false;
      return;
    }

    const same = JSON.stringify(metricsFromConfig) === JSON.stringify(draftMetrics);

    if (!same) {
      setDraftMetrics(metricsFromConfig);
    }
  }, [metricsFromConfig]);

  const syncToConfig = useCallback(
    (next: Metric[]) => {
      const valid = next.filter((m) => m.title.trim() !== "" && m.filter.trim() !== "");

      const cubics: Record<string, Metric> = {};

      valid.forEach((m, i) => {
        cubics[`cubic${i + 1}`] = {
          title: m.title,
          filter: m.filter,
          ...(m.filter === "PME" && m.indexName?.index !== DEAL_INDEX && m.indexName && { indexName: m.indexName })
        };
      });

      const nextConfig = {
        ...config,
        cubics: valid.length ? cubics : undefined
      };

      const same = JSON.stringify(config.cubics) === JSON.stringify(nextConfig.cubics);

      if (!same) {
        onUpdateConfig(nextConfig);
      }
    },
    [config, onUpdateConfig]
  );

  const handleAdd = useCallback(() => {
    setDraftMetrics((prev) => {
      if (prev.length >= 5) return prev;
      return [...prev, { title: "", filter: "" }];
    });
  }, []);

  const handleRemove = useCallback((index: number) => {
    setDraftMetrics((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdate = useCallback((index: number, updates: Partial<Metric>) => {
    setDraftMetrics((prev) => {
      const updated = [...prev];
      const current = updated[index] ?? { title: "", filter: "" };

      updated[index] = { ...current, ...updates };

      if (updates.filter !== undefined && (!current.title || current.title === current.filter)) {
        updated[index].title = updates.filter;
      }

      if (updates.filter !== undefined && updates.filter === "PME" && !current.indexName) {
        updated[index].indexName = { index: DEAL_INDEX, name: "Use deal specific index" };
      }

      if (updates.filter !== undefined && updates.filter !== "PME") {
        delete updated[index].indexName;
      }

      return updated;
    });
  }, []);

  useEffect(() => {
    syncToConfig(draftMetrics);
  }, [draftMetrics, syncToConfig]);

  return (
    <Box>
      <SectionLabel>KPI Metrics</SectionLabel>

      {draftMetrics.length === 0 && (
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", mb: 1 }}>
          No metrics configured. Add metrics to fetch data from the API.
        </Typography>
      )}

      {draftMetrics.map((metric, index) => (
        <Stack key={index} sx={{ flexDirection: "column", gap: "10px", mb: "10px" }}>
          <Stack flexDirection="row" alignItems="center" gap="4px">
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#9CA3AF", flex: 1 }}>
              Metric {index + 1}
            </Typography>

            <IconButton
              size="sm"
              onClick={() => handleRemove(index)}
              sx={{ p: "2px" }}
              disabled={draftMetrics.length <= 1}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>

          <SelectAutocomplete<string>
            options={kpiOptions}
            defaultValue={kpiOptions.includes(metric.filter) ? metric.filter : null}
            onChange={(value) => {
              if (value) handleUpdate(index, { filter: value });
            }}
            disableClearable
            renderInput={(params) => <TextField {...params} label="Value" size="small" />}
            limitTags={2}
          />

          {metric.filter === "PME" && (
            <SelectAutocomplete<string>
              options={indexOptions}
              defaultValue={metric.indexName?.name}
              disableClearable
              onChange={(selectedValue) => {
                if (!selectedValue) return;
                if (selectedValue === "Use deal specific index") {
                  handleUpdate(index, { indexName: { index: DEAL_INDEX, name: selectedValue } });
                  return;
                }
                const indexKey = Object.entries(indexNames).find(([, val]) => val === selectedValue)?.[0];
                if (indexKey) {
                  handleUpdate(index, { indexName: { index: indexKey, name: selectedValue } });
                }
              }}
              renderInput={(params) => <TextField {...params} label="Index" size="small" />}
              limitTags={2}
            />
          )}

          <TextField
            size="small"
            label="Rename"
            value={metric.title}
            onChange={(e) => handleUpdate(index, { title: e.target.value })}
          />
        </Stack>
      ))}
      <Button
        size="sm"
        disabled={draftMetrics.length >= 5}
        onClick={handleAdd}
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        sx={{
          fontSize: 12,
          textTransform: "none",
          color: "#26A6AE"
        }}
      >
        Add Metric
      </Button>
    </Box>
  );
};
